import { useState } from "react";
import DSBox from "views/system-design/DSBox";
import DSButton from "views/system-design/DSButton";
import DSChip from "views/system-design/DSChip";
import DSEnumSelect from "views/system-design/DSEnumSelect";
import DSPopover from "views/system-design/DSPopover";
import DSTextField from "views/system-design/DSTextField";
import DSTooltip from "views/system-design/DSTooltip";
import DSTypography from "views/system-design/DSTypography";
import { SilencedTarget } from "../../../api/admin";

// A fleet-wide incident can name hundreds of clocks. Rendering every one as a
// chip (each with its own tooltip) is what makes the dialog crawl, and nobody
// silences three hundred targets by clicking anyway — the rest stay reachable
// through the add field.
const MAX_VISIBLE_FAILING = 30;

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;

type ExpiryOption = "never" | "24h" | "7d" | "30d";

const EXPIRY_OPTIONS: { value: ExpiryOption; label: string }[] = [
  { value: "never", label: "Never expires" },
  { value: "24h", label: "Expires in 24h" },
  { value: "7d", label: "Expires in 7 days" },
  { value: "30d", label: "Expires in 30 days" },
];

// The same choices as the add row, sized for buttons in a popover.
const SHORT_EXPIRY_LABELS: Record<ExpiryOption, string> = {
  never: "Forever",
  "24h": "24h",
  "7d": "7 days",
  "30d": "30 days",
};

const EXPIRY_HOURS: Record<Exclude<ExpiryOption, "never">, number> = {
  "24h": 24,
  "7d": 7 * 24,
  "30d": 30 * 24,
};

const untilFrom = (option: ExpiryOption): number | null =>
  option === "never" ? null : Date.now() + EXPIRY_HOURS[option] * HOUR_MS;

const isExpired = (entry: SilencedTarget) =>
  entry.until !== null && entry.until <= Date.now();

const plural = (count: number, unit: string) =>
  `${count} ${unit}${count === 1 ? "" : "s"}`;

// "in 3 days" / "5 hours ago" / "just now" — enough for an operator to judge
// whether a silence is still pulling its weight, without a date library.
const relative = (timestamp: number): string => {
  const diffMinutes = Math.round(Math.abs(timestamp - Date.now()) / MINUTE_MS);
  if (diffMinutes < 1) return "just now";
  const amount =
    diffMinutes < 60
      ? plural(diffMinutes, "minute")
      : diffMinutes < 60 * 24
        ? plural(Math.round(diffMinutes / 60), "hour")
        : plural(Math.round(diffMinutes / (60 * 24)), "day");
  return timestamp > Date.now() ? `in ${amount}` : `${amount} ago`;
};

// The compact form that rides on the chip itself, so the expiry is readable
// without hovering. Null for a silence that never expires — no badge is the
// clearest way to say "permanent".
const remainingBadge = (entry: SilencedTarget): string | null => {
  if (entry.until === null) return null;
  const minutesLeft = Math.round((entry.until - Date.now()) / MINUTE_MS);
  if (minutesLeft <= 0) return "expired";
  if (minutesLeft < 60) return `${minutesLeft}m left`;
  if (minutesLeft < 60 * 24) return `${Math.round(minutesLeft / 60)}h left`;
  return `${Math.round(minutesLeft / (60 * 24))}d left`;
};

// `by` and `at` are stamped server-side, so an entry added in this dialog has
// neither until the write comes back — describing it as "silenced by  20711
// days ago" (the epoch) is worse than saying plainly that it is still in
// flight.
const isUnsaved = (entry: SilencedTarget) => !entry.at;

// What rides on the chip: the countdown, and — for an entry this dialog has
// added but not yet written — that it is still only local.
const chipBadge = (entry: SilencedTarget): string | null => {
  const remaining = remainingBadge(entry);
  if (isUnsaved(entry)) {
    return remaining ? `${remaining} · saving…` : "saving…";
  }
  return remaining;
};

const describe = (entry: SilencedTarget): string =>
  [
    entry.reason,
    isUnsaved(entry)
      ? "saving…"
      : `silenced by ${entry.by} ${relative(entry.at)}`,
    entry.until === null
      ? "never expires"
      : isExpired(entry)
        ? "expired — will be dropped on the next run"
        : `expires ${relative(entry.until)}`,
  ]
    .filter(Boolean)
    .join(" · ");

interface SilencedTargetsEditorProps {
  // The kind of entity the monitor reports, for labelling: 'order', 'clock'…
  targetType: string;
  silencedTargets: SilencedTarget[];
  onChange: (silencedTargets: SilencedTarget[]) => void;
  // Targets of the current incident — the one-click mute candidates.
  incidentTargets: string[];
  // Silences that actually matched on the last run.
  silencedHits: string[];
}

const SilencedTargetsEditor = ({
  targetType,
  silencedTargets,
  onChange,
  incidentTargets,
  silencedHits,
}: SilencedTargetsEditorProps) => {
  const [newTarget, setNewTarget] = useState("");
  const [newReason, setNewReason] = useState("");
  const [newExpiry, setNewExpiry] = useState<ExpiryOption>("never");
  // The failing target whose "for how long?" popover is open, and the chip it
  // hangs off. Clicking a failing chip asks before it silences, because
  // "forever" is the wrong default for most of these.
  const [pending, setPending] = useState<{
    target: string;
    anchor: HTMLElement;
  } | null>(null);
  const [pendingReason, setPendingReason] = useState("");

  const silenced = new Set(silencedTargets.map((entry) => entry.target.trim()));

  // `by` and `at` are stamped server-side on save; the placeholders here only
  // keep the chip renderable until then.
  const add = (target: string, reason: string, until: number | null) => {
    const trimmed = target.trim();
    const trimmedReason = reason.trim();
    // A silence with no reason is unreviewable six months later — nobody can
    // tell whether it is still justified, so it cannot be created without one.
    if (!trimmed || !trimmedReason || silenced.has(trimmed)) return;
    onChange([
      ...silencedTargets,
      { target: trimmed, reason: trimmedReason, until, by: "", at: 0 },
    ]);
  };

  const addFromForm = () => {
    add(newTarget, newReason, untilFrom(newExpiry));
    setNewTarget("");
    setNewReason("");
    setNewExpiry("never");
  };

  const closePending = () => {
    setPending(null);
    setPendingReason("");
  };

  const confirmPending = (option: ExpiryOption) => {
    if (pending) add(pending.target, pendingReason, untilFrom(option));
    closePending();
  };

  const remove = (target: string) =>
    onChange(silencedTargets.filter((entry) => entry.target !== target));

  const mutable = incidentTargets.filter((target) => !silenced.has(target));

  return (
    <DSBox sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <DSTypography variant="caption" color="text.secondary">
        Silenced {targetType}s ({silencedTargets.length}) — issues for these are
        ignored, and the monitor goes green if nothing else fails.
      </DSTypography>

      {silencedTargets.length === 0 ? (
        <DSTypography variant="body2" color="text.secondary">
          None
        </DSTypography>
      ) : (
        <DSBox sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {silencedTargets.map((entry) => (
            <DSTooltip key={entry.target} title={describe(entry)}>
              <DSChip
                label={
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "baseline",
                      gap: 6,
                    }}
                  >
                    <span>{entry.target}</span>
                    {chipBadge(entry) && (
                      <span style={{ opacity: 0.7, fontSize: "0.9em" }}>
                        {chipBadge(entry)}
                      </span>
                    )}
                  </span>
                }
                size="small"
                color={silencedHits.includes(entry.target) ? "info" : "default"}
                variant={isExpired(entry) ? "outlined" : "filled"}
                sx={{ opacity: isExpired(entry) ? 0.5 : 1 }}
                onDelete={() => remove(entry.target)}
              />
            </DSTooltip>
          ))}
        </DSBox>
      )}

      <DSBox sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        <DSTextField
          label={targetType}
          size="small"
          value={newTarget}
          onChange={(e) => setNewTarget(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addFromForm();
            }
          }}
          sx={{ flex: "1 1 140px" }}
        />
        <DSTextField
          label="Reason"
          required
          size="small"
          value={newReason}
          onChange={(e) => setNewReason(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addFromForm();
            }
          }}
          sx={{ flex: "1 1 180px" }}
        />
        <DSEnumSelect
          value={newExpiry}
          options={EXPIRY_OPTIONS}
          onChange={setNewExpiry}
          size="small"
        />
        <DSButton
          variant="outlined"
          size="small"
          disabled={!newTarget.trim() || !newReason.trim()}
          onClick={addFromForm}
        >
          Add
        </DSButton>
      </DSBox>

      {mutable.length > 0 && (
        <DSBox sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <DSTypography variant="caption" color="text.secondary">
            Currently failing ({mutable.length}) — click one to choose how long
            to silence it
            {mutable.length > MAX_VISIBLE_FAILING &&
              `, showing the first ${MAX_VISIBLE_FAILING}`}
            :
          </DSTypography>
          <DSBox sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {mutable.slice(0, MAX_VISIBLE_FAILING).map((target) => (
              <DSChip
                key={target}
                label={target}
                size="small"
                variant="outlined"
                color="error"
                onClick={(event) =>
                  setPending({ target, anchor: event.currentTarget })
                }
                sx={{ cursor: "pointer" }}
              />
            ))}
          </DSBox>
        </DSBox>
      )}

      {silencedHits.length > 0 && (
        <DSTypography variant="caption" color="text.secondary">
          Silenced on the last run: {silencedHits.join(", ")}
        </DSTypography>
      )}

      <DSPopover
        open={!!pending}
        anchorEl={pending?.anchor ?? null}
        onClose={closePending}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { p: 2, width: 260 } } }}
      >
        <DSBox sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <DSTypography variant="caption" color="text.secondary">
            Silence <strong>{pending?.target}</strong> for
          </DSTypography>
          <DSTextField
            label="Reason"
            required
            size="small"
            autoFocus
            value={pendingReason}
            onChange={(e) => setPendingReason(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && pendingReason.trim()) {
                e.preventDefault();
                confirmPending("30d");
              }
            }}
            helperText={
              pendingReason.trim()
                ? "Enter silences for 30 days"
                : "Why this one is expected — required"
            }
          />
          <DSBox sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {EXPIRY_OPTIONS.map((option) => (
              <DSButton
                key={option.value}
                variant={option.value === "never" ? "text" : "outlined"}
                size="small"
                disabled={!pendingReason.trim()}
                onClick={() => confirmPending(option.value)}
              >
                {SHORT_EXPIRY_LABELS[option.value]}
              </DSButton>
            ))}
          </DSBox>
        </DSBox>
      </DSPopover>
    </DSBox>
  );
};

export default SilencedTargetsEditor;
